/**
 * EquipesPage.tsx (service technique) — Bloc 2.
 *
 * Gestion des équipes de travail, fidèle au mockup validé :
 *   - liste des équipes (couleur + membres en pastilles, absents grisés côté
 *     planning) à gauche ;
 *   - éditeur (nom, couleur, membres cochables) à droite.
 * Les équipes alimentent l'affectation rapide du planning de travail.
 */

import { useEffect, useState } from 'react';
import { AppHeader } from '@/components/AppHeader';
import { api, ApiException } from '@/lib/api';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { ServiceTechniqueTabs } from '@/pages/service-technique/ServiceTechniqueTabs';
import type { PlanningEquipe, PlanningAgent, PlanningTemplate } from '@roronoa-collectivites/shared-types';

const PALETTE = ['#378ADD', '#1D9E75', '#BA7517', '#D4537E', '#7F77DD', '#D85A30', '#639922', '#534AB7'];

type Draft = { id: string | null; nom: string; couleur: string; membreIds: string[] };

export function EquipesServiceTechniquePage() {
  const { confirm, dialogElement } = useConfirmDialog();
  const [equipes, setEquipes] = useState<PlanningEquipe[]>([]);
  const [agents, setAgents] = useState<PlanningAgent[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Templates de travaux types
  const [templates, setTemplates] = useState<PlanningTemplate[]>([]);
  const [tDraft, setTDraft] = useState<{ id: string | null; titre: string; dureeMin: number; nbPersonnes: number } | null>(null);

  async function reload() {
    try {
      const [eq, week, tpl] = await Promise.all([
        api.serviceTechnique.planning.listEquipes(),
        api.serviceTechnique.planning.getWeek(new Date().toISOString().slice(0, 10)),
        api.serviceTechnique.planning.listTemplates().catch(() => [] as PlanningTemplate[]),
      ]);
      setEquipes(eq);
      setAgents(week.agents as unknown as PlanningAgent[]);
      setTemplates(tpl);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur de chargement');
    }
  }
  useEffect(() => { void reload(); }, []);

  const enregistrerTemplate = async () => {
    if (!tDraft || tDraft.titre.trim().length < 2) { setError('Titre du modèle trop court'); return; }
    try {
      const body = { titre: tDraft.titre.trim(), dureeMin: tDraft.dureeMin, nbPersonnes: tDraft.nbPersonnes };
      if (tDraft.id) await api.serviceTechnique.planning.updateTemplate(tDraft.id, body);
      else await api.serviceTechnique.planning.createTemplate(body);
      setTDraft(null);
      await reload();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur modèle'); }
  };
  const supprimerTemplate = async (id: string) => {
    const ok = await confirm({ title: 'Supprimer ce modèle ?', description: 'Il ne sera plus proposé à l\'affectation.', confirmLabel: 'Supprimer', danger: true });
    if (!ok) return;
    try { await api.serviceTechnique.planning.deleteTemplate(id); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  };

  const nouvelle = () => setDraft({ id: null, nom: '', couleur: PALETTE[0]!, membreIds: [] });
  const editer = (e: PlanningEquipe) => setDraft({ id: e.id, nom: e.nom, couleur: e.couleur, membreIds: e.membres.map((m) => m.id) });

  async function enregistrer() {
    if (!draft || draft.nom.trim().length < 2) { setError('Le nom doit faire au moins 2 caractères'); return; }
    setBusy(true); setError(null);
    try {
      const body = { nom: draft.nom.trim(), couleur: draft.couleur, membreIds: draft.membreIds };
      if (draft.id) await api.serviceTechnique.planning.updateEquipe(draft.id, body);
      else await api.serviceTechnique.planning.createEquipe(body);
      setDraft(null);
      await reload();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur à l\'enregistrement');
    } finally { setBusy(false); }
  }

  async function supprimer(id: string) {
    const ok = await confirm({ title: 'Supprimer l\'équipe ?', description: 'Cette équipe ne sera plus proposée dans l\'affectation. Les créneaux déjà posés ne sont pas modifiés.', confirmLabel: 'Supprimer', danger: true });
    if (!ok) return;
    try { await api.serviceTechnique.planning.deleteEquipe(id); if (draft?.id === id) setDraft(null); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur à la suppression'); }
  }

  const toggleMembre = (id: string) => {
    if (!draft) return;
    setDraft({ ...draft, membreIds: draft.membreIds.includes(id) ? draft.membreIds.filter((x) => x !== id) : [...draft.membreIds, id] });
  };

  const initials = (name: string) => name.split(' ').map((w) => w[0]).slice(0, 2).join('');

  return (
    <div className="min-h-screen bg-bg">
      <AppHeader module="Service technique" accent="tech" />
      <ServiceTechniqueTabs active="planning" />
      <main className="mx-auto max-w-[1080px] px-4 py-5">
        <h1 className="mb-1 text-lg font-bold text-ink">Équipes de travail</h1>
        <p className="mb-4 text-sm text-ink-muted">Créer des équipes types réutilisables · les absents s'affichent grisés dans le planning.</p>

        {error && <div className="mb-4 rounded-r-lg border border-urgent/40 bg-urgent/10 px-4 py-2.5 text-sm text-urgent">{error}</div>}

        <div className="grid items-start gap-4 lg:grid-cols-2">
          {/* Liste des équipes */}
          <div className="rounded-r-xl border border-border bg-surface/55 shadow-soft">
            <div className="flex items-center justify-between px-4 pt-4">
              <span className="text-xs font-semibold uppercase tracking-r-eyebrow text-ink-subtle">Équipes du service</span>
              <button type="button" onClick={nouvelle} className="rounded-r-md bg-tech px-3 py-1.5 text-xs font-semibold text-white transition hover:opacity-90">＋ Nouvelle équipe</button>
            </div>
            <div className="flex flex-col gap-2.5 p-3.5">
              {equipes.length === 0 && <p className="px-1 py-4 text-sm text-ink-muted">Aucune équipe. Crée la première.</p>}
              {equipes.map((e) => (
                <div key={e.id} className={['rounded-r-lg border bg-bg-2/60 p-3', draft?.id === e.id ? 'border-tech' : 'border-border-hi'].join(' ')}>
                  <div className="mb-2 flex items-center gap-2.5">
                    <span className="h-3 w-3 rounded" style={{ backgroundColor: e.couleur }} />
                    <span className="text-sm font-bold text-ink">{e.nom}</span>
                    <span className="ml-auto text-xs text-ink-subtle">{e.membres.length} membre{e.membres.length > 1 ? 's' : ''}</span>
                    <button type="button" onClick={() => editer(e)} className="px-1.5 text-ink-subtle transition hover:text-tech-fg" aria-label="Modifier">✎</button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {e.membres.map((m) => (
                      <span key={m.id} className="flex items-center gap-1.5 rounded-r-pill border border-border bg-surface py-0.5 pl-0.5 pr-2.5 text-xs">
                        <span className="grid h-5 w-5 place-items-center rounded-full text-[9px] font-bold text-white" style={{ backgroundColor: m.couleurMarker ?? '#A8A29E' }}>{initials(m.fullName)}</span>
                        {m.fullName.split(' ')[0]}
                      </span>
                    ))}
                    {e.membres.length === 0 && <span className="text-2xs text-ink-dim">Aucun membre</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Éditeur */}
          <div className="rounded-r-xl border border-border bg-surface/55 shadow-soft">
            {!draft ? (
              <div className="p-8 text-center text-sm text-ink-muted">Sélectionne une équipe à modifier, ou crée-en une nouvelle.</div>
            ) : (
              <>
                <div className="border-b border-border px-4 py-3.5">
                  <h3 className="text-sm font-bold text-ink">{draft.id ? 'Modifier l\'équipe' : 'Nouvelle équipe'}</h3>
                  <p className="mt-0.5 text-2xs text-ink-subtle">Les changements s'appliquent aux prochaines affectations.</p>
                </div>
                <div className="flex flex-col gap-4 px-4 py-4">
                  <div>
                    <label className="mb-1.5 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Nom de l'équipe</label>
                    <input value={draft.nom} onChange={(e) => setDraft({ ...draft, nom: e.target.value })} className="w-full rounded-r-md border border-border-hi bg-bg-2/60 px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech" placeholder="Ex. Équipe Voirie" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Couleur</label>
                    <div className="flex gap-2">
                      {PALETTE.map((c) => (
                        <button key={c} type="button" onClick={() => setDraft({ ...draft, couleur: c })} className={['h-7 w-7 rounded-r-md border-2 transition', draft.couleur === c ? 'border-white' : 'border-transparent'].join(' ')} style={{ backgroundColor: c }} aria-label={`Couleur ${c}`} />
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Membres (agents & managers du service)</label>
                    <div className="grid grid-cols-2 gap-2">
                      {agents.map((a) => {
                        const sel = draft.membreIds.includes(a.id);
                        return (
                          <button key={a.id} type="button" onClick={() => toggleMembre(a.id)} className={['flex items-center gap-2 rounded-r-md border px-2.5 py-2 text-left transition', sel ? 'border-tech bg-tech-tint/50' : 'border-border-hi bg-bg-2/60'].join(' ')}>
                            <span className="grid h-6 w-6 place-items-center rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: a.couleurMarker ?? '#A8A29E' }}>{initials(a.fullName)}</span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-xs font-semibold text-ink">{a.fullName.split(' ')[0]}</span>
                              <span className="block text-[9.5px] text-ink-subtle">{a.role === 'manager' ? 'Manager' : 'Agent'}</span>
                            </span>
                            <span className={['grid h-4 w-4 place-items-center rounded border text-[10px] text-white', sel ? 'border-tech bg-tech' : 'border-border-hi'].join(' ')}>{sel ? '✓' : ''}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between border-t border-border px-4 py-3.5">
                  {draft.id ? (
                    <button type="button" onClick={() => supprimer(draft.id!)} className="text-xs font-semibold text-urgent">🗑 Supprimer</button>
                  ) : <span />}
                  <div className="flex gap-2.5">
                    <button type="button" onClick={() => setDraft(null)} className="rounded-r-md border border-border-hi bg-bg-2/60 px-4 py-2 text-sm font-semibold text-ink">Annuler</button>
                    <button type="button" onClick={enregistrer} disabled={busy} className="rounded-r-md bg-tech px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{busy ? '…' : 'Enregistrer'}</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ===== Modèles de travaux types ===== */}
        <div className="mt-6 rounded-r-xl border border-border bg-surface/55 shadow-soft">
          <div className="flex items-center justify-between px-4 pt-4">
            <div>
              <span className="text-xs font-semibold uppercase tracking-r-eyebrow text-ink-subtle">Modèles de travaux types</span>
              <p className="mt-0.5 text-2xs text-ink-dim">Ex. « Tonte stade » · 2h · 3 personnes. Glissables sur le planning pour pré-remplir l'affectation.</p>
            </div>
            <button type="button" onClick={() => setTDraft({ id: null, titre: '', dureeMin: 120, nbPersonnes: 1 })} className="rounded-r-md bg-tech px-3 py-1.5 text-xs font-semibold text-white transition hover:opacity-90">＋ Nouveau modèle</button>
          </div>
          <div className="flex flex-col gap-2 p-3.5">
            {templates.length === 0 && !tDraft && <p className="px-1 py-3 text-sm text-ink-muted">Aucun modèle. Crée-en un pour accélérer l'affectation des tâches récurrentes.</p>}
            {tDraft && (
              <div className="rounded-r-lg border border-tech bg-bg-2/60 p-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr_1fr]">
                  <div>
                    <label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Titre</label>
                    <input value={tDraft.titre} onChange={(e) => setTDraft({ ...tDraft, titre: e.target.value })} placeholder="Tonte stade" className="w-full rounded-r-md border border-border-hi bg-surface/60 px-3 py-2 text-sm text-ink" />
                  </div>
                  <div>
                    <label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Durée (min)</label>
                    <input type="number" min={15} max={1440} step={15} value={tDraft.dureeMin} onChange={(e) => setTDraft({ ...tDraft, dureeMin: Number(e.target.value) })} className="w-full rounded-r-md border border-border-hi bg-surface/60 px-3 py-2 text-sm text-ink" />
                  </div>
                  <div>
                    <label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Nb personnes</label>
                    <input type="number" min={1} max={50} value={tDraft.nbPersonnes} onChange={(e) => setTDraft({ ...tDraft, nbPersonnes: Number(e.target.value) })} className="w-full rounded-r-md border border-border-hi bg-surface/60 px-3 py-2 text-sm text-ink" />
                  </div>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <button type="button" onClick={() => setTDraft(null)} className="rounded-r-md border border-border-hi bg-bg-2/60 px-3 py-1.5 text-sm font-semibold text-ink">Annuler</button>
                  <button type="button" onClick={enregistrerTemplate} className="rounded-r-md bg-tech px-3 py-1.5 text-sm font-semibold text-white">Enregistrer</button>
                </div>
              </div>
            )}
            {templates.map((t) => (
              <div key={t.id} className="flex items-center gap-3 rounded-r-lg border border-border-hi bg-bg-2/60 px-3 py-2.5">
                <span className="flex-1 text-sm font-semibold text-ink">{t.titre}</span>
                <span className="rounded-r-pill border border-border bg-surface px-2 py-0.5 text-2xs text-ink-muted">{Math.round(t.dureeMin / 60 * 10) / 10}h</span>
                <span className="rounded-r-pill border border-border bg-surface px-2 py-0.5 text-2xs text-ink-muted">{t.nbPersonnes} pers.</span>
                <button type="button" onClick={() => setTDraft({ id: t.id, titre: t.titre, dureeMin: t.dureeMin, nbPersonnes: t.nbPersonnes })} className="px-1.5 text-ink-subtle transition hover:text-tech-fg" aria-label="Modifier">✎</button>
                <button type="button" onClick={() => supprimerTemplate(t.id)} className="px-1.5 text-ink-subtle transition hover:text-urgent" aria-label="Supprimer">🗑</button>
              </div>
            ))}
          </div>
        </div>
      </main>
      {dialogElement}
    </div>
  );
}
